import React, { useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { FaWhatsapp, FaEnvelope, FaTimes } from "react-icons/fa";
import "./OsiligiSupportForm.css";

const OsiligiSupportForm = ({ onClose }) => {
  const { t } = useTranslation("osiligi");
  const [showThankYou, setShowThankYou] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    fullName: "",
    representing: "Myself",
    email: "",
    involvementTime: "As soon as possible",
    phone: "",
    countryCity: "",
    supportMethods: {
      volunteering: false,
      medicalSupplies: false,
      donatingFunds: false,
      partnering: false,
      expertise: false,
      other: false,
    },
    interestDescription: "",
    consentGiven: false,
  });

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      supportMethods: {
        ...prev.supportMethods,
        [name]: checked,
      },
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === "consentGiven") {
      setFormData((prev) => ({ ...prev, consentGiven: checked }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.consentGiven) {
      alert(
        "Please agree to be contacted by the Osiligi Medical Foundation team."
      );
      // But do NOT return; let submission proceed anyway
    }

    // Prepare data to send
    const dataToSend = {
      fullName: formData.fullName,
      representing: formData.representing,
      email: formData.email,
      involvementTime: formData.involvementTime,
      phone: formData.phone,
      countryCity: formData.countryCity,
      supportMethods: Object.entries(formData.supportMethods)
        .filter(([_, v]) => v)
        .map(([k]) => k),
      interestDescription: formData.interestDescription,
      consentGiven: formData.consentGiven,
    };

    try {
     
      const response = await fetch("/backend/osiligisupport", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(dataToSend),
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      setShowThankYou(true);

      setTimeout(() => {
        setShowThankYou(false);
        onClose?.();
      }, 6000);
    } catch (error) {
      alert(
        "There was an error sending your message. Please try again later."
      );
      console.error("Submission error:", error);
    }
  };

  return (
    <div className="osiligi-support-overlay">
      <div className="osiligi-support-modal">
        <button className="osiligi-close-button" onClick={onClose}>
          <FaTimes />
        </button>

        <section className="osiligi-form-wrapper">
          {!showThankYou ? (
            <>
              <div className="osiligi-form-header">
                <h2>{t("supportHero.title", { ns: "osiligi" })}</h2>
                <p>
                  <Trans i18nKey="supportHero.description" ns="osiligi" />
                </p>
                <p>
                  <Trans i18nKey="supportForm.description" ns="osiligi" />
                </p>
              </div>

              <form className="osiligi-form" onSubmit={handleSubmit}>
                <div className="osiligi-row">
                  <div className="osiligi-field">
                    <label>Full Name</label>
                    <input
                      type="text"
                      name="fullName"
                      placeholder="Enter your name"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="osiligi-field">
                    <label>I'm representing...</label>
                    <select
                      name="representing"
                      value={formData.representing}
                      onChange={handleChange}
                      required
                    >
                      <option>Myself</option>
                      <option>An Organization</option>
                      <option>A Donor / Foundation</option>
                      <option>A Medical Team</option>
                    </select>
                  </div>
                </div>

                <div className="osiligi-row">
                  <div className="osiligi-field">
                    <label>Email Address</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="osiligi-field">
                    <label>When would you like to get involved?</label>
                    <select
                      name="involvementTime"
                      value={formData.involvementTime}
                      onChange={handleChange}
                      required
                    >
                      <option>As soon as possible</option>
                      <option>Next medical camp (June/July 2026)</option>
                      <option>Later this year</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>

                <div className="osiligi-row">
                  <div className="osiligi-field">
                    <label>Phone / WhatsApp</label>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Enter your contact number"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="osiligi-field">
                    <label>Country / City</label>
                    <input
                      type="text"
                      name="countryCity"
                      placeholder="e.g. Nairobi, Kenya"
                      value={formData.countryCity}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </div>

                <div className="osiligi-field">
                  <label>I’d love to support by:</label>
                  <div className="osiligi-checkboxes">
                    <label>
                      <input
                        type="checkbox"
                        name="volunteering"
                        checked={formData.supportMethods.volunteering}
                        onChange={handleCheckboxChange}
                      />{" "}
                      Volunteering on-site
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        name="medicalSupplies"
                        checked={formData.supportMethods.medicalSupplies}
                        onChange={handleCheckboxChange}
                      />{" "}
                      Providing medical supplies
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        name="donatingFunds"
                        checked={formData.supportMethods.donatingFunds}
                        onChange={handleCheckboxChange}
                      />{" "}
                      Donating funds or equipment
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        name="partnering"
                        checked={formData.supportMethods.partnering}
                        onChange={handleCheckboxChange}
                      />{" "}
                      Partnering institutionally
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        name="expertise"
                        checked={formData.supportMethods.expertise}
                        onChange={handleCheckboxChange}
                      />{" "}
                      Offering professional expertise
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        name="other"
                        checked={formData.supportMethods.other}
                        onChange={handleCheckboxChange}
                      />{" "}
                      Other
                    </label>
                  </div>
                </div>

                <div className="osiligi-field">
                  <label>Please describe your interest or what you’d like to offer</label>
                  <textarea
                    name="interestDescription"
                    rows="3"
                    placeholder="Write here..."
                    value={formData.interestDescription}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="osiligi-field osiligi-consent">
                  <label>
                    <input
                      type="checkbox"
                      name="consentGiven"
                      checked={formData.consentGiven}
                      onChange={handleChange}
                      required
                    />{" "}
                    I agree to be contacted by the Osiligi Medical Foundation team.
                  </label>
                </div>

                <button type="submit" className="osiligi-submit-button">
                  Send My Interest
                </button>
              </form>

              <div className="osiligi-contact">
                <h3>Need Help?</h3>
                <p>
                  Reach out directly to our support team — we’d love to guide you
                  through how you can contribute.
                </p>
                <div className="osiligi-contact-links">
                  <a
                    href="https://wa.me/254702965315?text=Osiligi%20Medical%20Foundation%20partners%20with%20compassionate%20individuals%20and%20organizations%20to%20extend%20healthcare%20access%20in%20Maasai%20communities.%20Tell%20us%20how%20you%E2%80%99d%20like%20to%20be%20part%20of%20this%20mission."
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaWhatsapp /> Chat on WhatsApp
                  </a>
                  <a href="mailto:kikanae@oleninisafaris.com">
                    <FaEnvelope /> Email Us
                  </a>
                </div>
              </div>
            </>
          ) : (
            <div className="osiligi-thank-you">
              <h3>Thank You for Reaching Out!</h3>
              <p>
                Your message has been received. The Osiligi Medical Foundation
                team truly appreciates your interest — we’ll reach out to you
                shortly.
              </p>
              <p>
                We’re grateful to have you as part of Osiligi’s mission of healing
                and hope.
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default OsiligiSupportForm;
